import { useState } from "react";
import { Alert, DialogActions, DialogContent, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useTemplateAvailableActions } from "src/api/client-api/actionPlanTemplates/useTemplateActionOptions";
import { groupTemplateCatalog } from "./templateActionCatalog";
import { TemplateActionCategory } from "./TemplateActionCategory";
import { TemplateActionCategories } from "./TemplateActionCategories";

type Props = {
    ssqVersionId: number | undefined;
    addedIds: number[];
    onSelect: (actionId: number) => void;
    onClose: () => void;
};

export const TemplateActionCatalogStep = (props: Props) => {
    const { ssqVersionId, addedIds, onSelect, onClose } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const catalog = useTemplateAvailableActions(ssqVersionId);
    const [categoryId, setCategoryId] = useState<number>();

    const categories = groupTemplateCatalog(
        catalog.data ?? [],
        addedIds,
        t("action_plan.action.not_categorized", "Not categorized"),
    );
    const category = categories.find((item) => item.id === categoryId);
    const added = new Set(addedIds);

    const saveLabel = t("settings.action_plan_templates.editor.save", "Save");
    const cancelLabel = t("settings.action_plan_templates.editor.cancel", "Cancel");

    return (
        <>
            <DialogContent sx={{ p: theme.spacing(4) }}>
                {catalog.isLoading && (
                    <Typography component="p" variant="body-regular" role="status">
                        {t("settings.action_plan_templates.editor.loading_catalog", "Loading action catalog…")}
                    </Typography>
                )}
                {catalog.isError && (
                    <Alert
                        severity="error"
                        action={
                            <Button variant="text" onClick={() => catalog.refetch()}>
                                {t("settings.action_plan_templates.retry", "Try again")}
                            </Button>
                        }
                    >
                        {t(
                            "settings.action_plan_templates.editor.catalog_failed",
                            "Unable to load the action catalog. Please try again.",
                        )}
                    </Alert>
                )}
                {!catalog.isLoading && !catalog.isError && categories.length === 0 && (
                    <Typography component="p" variant="body-regular">
                        {t("settings.action_plan_templates.editor.empty_catalog", "No actions available for this SSQ.")}
                    </Typography>
                )}
                {category ?
                    <TemplateActionCategory
                        category={category}
                        added={added}
                        onBack={() => setCategoryId(undefined)}
                        onSelect={onSelect}
                    />
                :   <TemplateActionCategories categories={categories} onSelect={setCategoryId} />}
            </DialogContent>
            <DialogActions
                sx={{
                    p: theme.spacing(3),
                    justifyContent: "space-between",
                    borderTop: 1,
                    borderColor: "divider",
                }}
            >
                <Button variant="text" onClick={onClose} endIcon={<FoodsIcon icon="close" size={1} />}>
                    {cancelLabel}
                </Button>
                <Button variant="primary" disabled startIcon={<FoodsIcon icon="check" size={1} />}>
                    {saveLabel}
                </Button>
            </DialogActions>
        </>
    );
};
